import { useEffect, useRef, useState } from 'react';
import { CheckCircleIcon, EnvelopeSimpleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { useSearchParams } from 'react-router-dom';
import {
  PublicConsentButton,
  PublicConsentPage,
  brandName,
} from '../components/public-consent-page';
import { getUnsubscribe, unsubscribe } from '../services/public-consent.service';
import type { PublicConsentData, UnsubscribeState } from '../types/public-consent.types';

type ViewState = UnsubscribeState | 'already-unsubscribed' | 'loading' | 'submitting' | 'error';

export function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [data, setData] = useState<PublicConsentData<UnsubscribeState>>();
  const [state, setState] = useState<ViewState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [loadedToken, setLoadedToken] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    const currentRequest = ++requestId.current;
    setData(undefined);
    setLoadedToken('');
    setErrorMessage('');
    setState('loading');
    if (!token) {
      setState('invalid');
      return;
    }
    void getUnsubscribe(token)
      .then((response) => {
        if (requestId.current !== currentRequest) return;
        setData(response);
        setLoadedToken(token);
        setState(response.state === 'unsubscribed' ? 'already-unsubscribed' : response.state);
        document.documentElement.lang = response.locale;
      })
      .catch((error: unknown) => {
        if (requestId.current !== currentRequest) return;
        setErrorMessage(error instanceof Error ? error.message : 'Unable to load this link.');
        setState('error');
      });
  }, [token]);

  const submit = async () => {
    if (state !== 'unsubscribable' || loadedToken !== token) return;
    const currentRequest = ++requestId.current;
    setState('submitting');
    try {
      const response = await unsubscribe(token);
      if (requestId.current !== currentRequest) return;
      setData(response);
      setState(response.state);
    } catch (error) {
      if (requestId.current !== currentRequest) return;
      setErrorMessage(error instanceof Error ? error.message : 'Unable to unsubscribe.');
      setState('error');
    }
  };

  const name = brandName(data?.branding);
  const redirectUrl = data?.redirectUrl || data?.branding.unsubscribedRedirectUrl;
  const visibleState = loadedToken && loadedToken !== token ? 'loading' : state;

  return (
    <PublicConsentPage branding={data?.branding}>
      {visibleState === 'loading' ? (
        <div className="public-consent-loading" role="status">
          Loading…
        </div>
      ) : visibleState === 'unsubscribed' ? (
        <div className="public-consent-result public-consent-result--success">
          <CheckCircleIcon size={54} weight="duotone" />
          <h1>You&apos;ve been unsubscribed</h1>
          <p>
            You won&apos;t receive any more marketing emails from {name}. It can take a few minutes to
            apply everywhere.
          </p>
          {redirectUrl ? (
            <a className="public-consent-button" href={redirectUrl}>
              Continue to {name}
            </a>
          ) : null}
        </div>
      ) : visibleState === 'already-unsubscribed' ? (
        <div className="public-consent-result public-consent-result--success">
          <CheckCircleIcon size={54} weight="duotone" />
          <h1>You&apos;re already unsubscribed</h1>
          <p>
            There&apos;s nothing more to do — you already opted out of marketing emails from {name}.
          </p>
          {redirectUrl ? (
            <a className="public-consent-button" href={redirectUrl}>
              Continue to {name}
            </a>
          ) : null}
        </div>
      ) : visibleState === 'invalid' ? (
        <div className="public-consent-result public-consent-result--warning">
          <WarningCircleIcon size={54} weight="duotone" />
          <h1>This link is no longer valid</h1>
          <p>
            It may have expired or already been used. Use the unsubscribe link in a more recent
            message.
          </p>
        </div>
      ) : visibleState === 'error' ? (
        <div className="public-consent-result public-consent-result--warning">
          <WarningCircleIcon size={54} weight="duotone" />
          <h1>We couldn&apos;t load this page</h1>
          <p>{errorMessage}</p>
        </div>
      ) : (
        <div className="public-consent-content">
          <h1>Unsubscribe from {name}</h1>
          <p>
            You&apos;ll stop receiving marketing emails from {name}. This takes effect immediately and
            you don&apos;t need to log in.
          </p>
          <div className="public-consent-channel">
            <EnvelopeSimpleIcon size={21} />
            <span>
              <strong>Marketing emails</strong>
              <small>Newsletters, campaigns and promotions.</small>
            </span>
          </div>
          <PublicConsentButton disabled={visibleState === 'submitting'} onClick={submit}>
            {visibleState === 'submitting' ? 'Unsubscribing…' : 'Unsubscribe'}
          </PublicConsentButton>
        </div>
      )}
    </PublicConsentPage>
  );
}
